import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react';
import {
  getOtsFormControlClassName,
  OtsFormField,
  OtsFormFieldSize,
} from './OtsFormField';

export type OtsSearchInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: OtsFormFieldSize;
  containerClassName?: string;
  onClear?: () => void;
  /** Keeps the label for screen readers only, e.g. in a toolbar. */
  hideLabel?: boolean;
};

/** Foundational themed search field with an optional one-click clear action. */
export const OtsSearchInput = forwardRef<HTMLInputElement, OtsSearchInputProps>(
  function OtsSearchInput(
    {
      label,
      description,
      error,
      size = 'default',
      id,
      className,
      containerClassName,
      value,
      disabled,
      required,
      onClear,
      hideLabel,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) {
    const generatedId = useId();
    const inputId = id ?? `ots-search-${generatedId}`;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId]
      .filter(Boolean)
      .join(' ');
    const canClear = value !== undefined && String(value).length > 0 && onClear;

    return (
      <OtsFormField
        htmlFor={inputId}
        label={label}
        description={description}
        descriptionId={descriptionId}
        error={error}
        errorId={errorId}
        required={required}
        size={size}
        hideLabel={hideLabel}
        className={containerClassName}
      >
        <div className="relative">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-[var(--ots-text-muted)]"
          />
          <input
            {...props}
            ref={ref}
            id={inputId}
            type="search"
            value={value}
            disabled={disabled}
            required={required}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={describedBy || undefined}
            className={getOtsFormControlClassName(
              size,
              [
                'pl-10 [&::-webkit-search-cancel-button]:hidden',
                canClear ? 'pr-10' : '',
                className,
              ]
                .filter(Boolean)
                .join(' ')
            )}
          />
          {canClear && (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              aria-label="Clear search"
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-[var(--ots-text-muted)] transition-colors hover:text-[var(--ots-primary)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--ots-primary)] disabled:pointer-events-none disabled:opacity-50"
            >
              <XMarkIcon aria-hidden="true" className="size-[18px]" />
            </button>
          )}
        </div>
      </OtsFormField>
    );
  }
);
